import { useEffect, useRef, useState } from 'react';
import { app, banner, describeError, go, resetWizard, type Entry } from '../state/app';
import { getCharacters, getStarters } from '../api/endpoints';
import type { Character, GateVerdict, Starter } from '../api/types';
import { mediaUrl, starterPortraitKey } from '../lib/media';
import { lookup, reconcileCatalogVersion, remember } from '../lib/manifest';
import { once } from '../lib/once';
import {
  cancelGateSpeculation,
  gate,
  maybeSpeculateDraft,
  scheduleGateSpeculation,
} from '../state/speculate';
import { TierDot } from '../components/TierDot';
import { DEBUG } from '../runtime';

type GateBox =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'prechecked'; verdict: GateVerdict }
  | { kind: 'denied'; verdict: GateVerdict }
  | { kind: 'matched'; label: string }
  | { kind: 'error'; message: string };

/**
 * Screen 0. THREE entry paths now, and they are not equals.
 *
 * The family's own cast comes first, because a character they have already
 * paid to have drawn is the one thing on this screen that costs nothing more
 * to reuse and cannot be replaced by anything else here. Behind it, the
 * pre-baked tree for speed (zero gate calls, zero image spend) and freetext
 * for "a purple unicorn in a field", which converge on the same Entry and the
 * same spend key.
 *
 * The own-character path deliberately does NOT produce an Entry — see
 * pickMine, which is where the money in this file is.
 */
export function Gallery() {
  const { catalog, catalogError } = app.use();
  const [text, setText] = useState('');
  const [box, setBox] = useState<GateBox>({ kind: 'idle' });
  const [deviceKnown, setDeviceKnown] = useState<Set<string>>(new Set());
  // Slugs whose portrait 404'd. Until the pre-bake job runs, that is all 18 —
  // hiding the <img> alone would collapse the tile to a bare label.
  const [brokenArt, setBrokenArt] = useState<Set<string>>(new Set());
  // The same, for the family's own characters, keyed on characterId.
  const [brokenMine, setBrokenMine] = useState<Set<string>>(new Set());
  const [mine, setMine] = useState<Character[]>([]);
  const [mineError, setMineError] = useState<string | null>(null);
  const textRef = useRef('');
  textRef.current = text;

  useEffect(() => {
    let alive = true;
    // once(): StrictMode's double-mount, or a back-navigation, must not
    // re-fetch a catalog that is already in flight.
    once('catalog:starters', getStarters)
      .then((cat) => {
        if (!alive) return;
        const purged = reconcileCatalogVersion(cat.catalogVersion);
        // Snapshot device-tier knowledge BEFORE writing this run's entries,
        // otherwise every tile claims "on device" on its first ever paint.
        const known = new Set(
          cat.starters.filter((s) => lookup(`starter:${s.slug}`)).map((s) => s.slug),
        );
        for (const s of cat.starters) {
          remember(`starter:${s.slug}`, s.portraitKey ?? starterPortraitKey(s.slug), 'catalog');
        }
        setDeviceKnown(known);
        app.set({ catalog: cat, catalogError: null });
        if (purged && cat.catalogVersion !== 'unversioned') {
          banner(`catalog re-baked (${cat.catalogVersion}) — device manifest purged`);
        }
      })
      .catch((err) => {
        if (alive) app.set({ catalogError: describeError(err) });
      });
    return () => {
      alive = false;
      cancelGateSpeculation();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    // DELIBERATELY NOT once(), unlike the catalog fetch above.
    //
    // once() never evicts on success — that is the whole double-spend guard —
    // and this is the one list in the app whose entire purpose is to be FRESH.
    // Memoizing it would mean a parent who makes a character, lands back on
    // this screen and looks for it is shown a list captured before it existed:
    // the exact bug this section was added to fix, one layer down. The call is
    // a free, side-effect-free GET, so StrictMode's double mount costs a read
    // and nothing else.
    getCharacters()
      .then((rows) => {
        if (!alive) return;
        setMine(rows);
        setMineError(null);
      })
      .catch((err) => {
        // Never fatal to the screen. The catalog and the freetext box are both
        // still usable, so this reports what is missing and leaves the rest of
        // the gallery alone — it is an addition to this screen, not its floor.
        if (alive) setMineError(describeError(err));
      });
    return () => {
      alive = false;
    };
  }, []);

  const enter = (entry: Entry) => {
    // resetWizard() rather than the partial clear this used to do. It cleared
    // entry/characterId/character/chosenIndex and left `characterName` behind,
    // so starting a second character after finishing a first one carried the
    // FIRST one's name into the naming screen — pre-seeded, and with the
    // autofocus suppressed because NameIt reads a non-empty name as "you are
    // coming back to a character that is already named". A stale commissionId
    // likewise survived into a fresh wizard run. Starting a new character is
    // exactly what resetWizard means.
    resetWizard();
    app.set({ entry });
    go('candidates');
  };

  /**
   * A CHARACTER THAT ALREADY EXISTS SKIPS THE TWO SCREENS THAT MAKE ONE.
   *
   * The catalog path above goes to `candidates`, whose whole job is to call
   * draft() -> POST /characters and then wait for four poses to be drawn
   * (~$0.20 on the freetext path). This character has its four poses, has had
   * one of them chosen, and has a name locked — characters.py:_select takes
   * the pose and the name in a single write and answers 409 once the row is
   * `ready`. So there is nothing left for `candidates` or `name` to do except
   * ask questions that are already answered and pay to redraw art the family
   * already owns. The tap lands on `summary`, the screen that turns a
   * character into a film, which is the only step still outstanding.
   *
   * `entry` STAYS NULL, and that is load-bearing twice over:
   *
   *   * it is the first thing Candidates.tsx checks (`if (!entry) go('gallery')`),
   *     so a back gesture off the summary walks name -> candidates -> gallery
   *     without ever reaching draft(). Backing up must not mint a character.
   *   * an entry carrying this character's `canonical` instead would make that
   *     same walk create a SECOND character for the same description and pay
   *     for its art — the silent double-spend this screen exists to avoid.
   *
   * An entry with a label but NEITHER key is worse than either: lib/keys.ts's
   * draftKey throws synchronously inside Candidates' effect, which is a blank
   * screen rather than a wasted 20c.
   *
   * Nothing here starts a peek. state/speculate's pre-warm fires on the
   * CHILD's tap in the candidate chooser, and there is no such tap on this
   * path; the summary offers its "Sneak peek — free" button in the normal way
   * and spends a preview token only when the parent presses it.
   */
  const pickMine = (character: Character) => {
    resetWizard();
    app.set({
      characterId: character.characterId,
      character,
      chosenIndex: chosenPosition(character),
      // The locked name. `name` is _view's alias for it and agrees on every
      // commissionable row; the canonical fallback behind that field can only
      // surface on a character this list does not show. See ownCharacters().
      characterName: character.characterName || character.name || '',
    });
    go('summary');
  };

  const pickStarter = (starter: Starter) => {
    // Path A spends nothing: a catalog item is pre-vetted, so no gate call,
    // and its art is already baked.
    enter({
      starterSlug: starter.slug,
      label: starter.label,
      desc: starter.desc,
      canonical: starter.canonical,
      portraitUrl: portraitOf(starter),
      tier: deviceKnown.has(starter.slug) ? 'device' : 'catalog',
    });
  };

  const pickCustom = (canonical: string, typed: string) => {
    // `label` is what the PARENT sees; `canonical` is the cache key.
    //
    // They were the same value, so typing "an irish purple unicorn" put
    // "unicorn purple irish" — a sorted token string built for the aliases
    // table — into the heading and the preview pane. Correct as a key, and
    // gibberish as a title.
    enter({ label: typed.trim(), canonical, tier: 'generated' });
  };

  const onType = (value: string) => {
    setText(value);
    if (box.kind !== 'denied') setBox({ kind: 'idle' });
    scheduleGateSpeculation(
      () => textRef.current,
      (gatedText, verdict) => {
        // Free result, so keep it whatever happened. The 20c of images is
        // what the stale guard protects (R5), and that lives in speculate.ts.
        maybeSpeculateDraft(verdict, gatedText, textRef.current);
        if (textRef.current === gatedText && verdict.verdict === 'allow') {
          setBox({ kind: 'prechecked', verdict });
        }
      },
    );
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    cancelGateSpeculation();
    setBox({ kind: 'checking' });
    try {
      // Collapses onto the speculative call when the text has not changed.
      const verdict = await gate(value, false);
      if (verdict.verdict !== 'allow') {
        setBox({ kind: 'denied', verdict });
        return;
      }
      const match = matchStarter(catalog?.starters, verdict.catalog);
      if (match) {
        // Both a delight beat and a margin beat: nothing to generate.
        setBox({ kind: 'matched', label: match.label });
        setTimeout(() => pickStarter(match), 900);
        return;
      }
      setBox({ kind: 'idle' });
      // The gate's `suggestion` is the parent's idea tidied up; their raw
      // text is the fallback. Never the canonical.
      pickCustom(verdict.canonical || value.toLowerCase(),
                 verdict.suggestion || value);
    } catch (err) {
      setBox({ kind: 'error', message: describeError(err) });
    }
  };

  const starters = catalog?.starters ?? [];
  const ours = ownCharacters(mine);

  return (
    <>
      <div className="pane-img">
        <div className="placeholder">
          {starters.length
            ? '✨ tap someone they love — or describe your own'
            : 'loading the catalog…'}
        </div>
      </div>
      <div className="pane-q">
        <h1>Who does your kid love?</h1>
        {/* "Pre-baked and cached" was how WE talk about the catalog. A parent
            does not know what a cache is and should never have to — what they
            need to know is that tapping is free and instant. */}
        <p className="sub">
          Tap one — it's free and ready straight away. Or describe your own and
          we'll make them.
        </p>

        {catalogError && <div className="err">catalog: {catalogError}</div>}
        {mineError && <div className="err">your characters: {mineError}</div>}

        {/* ONE SCROLLER AROUND BOTH GRIDS. `.gallery` was the flex child that
            scrolled; two of them as siblings in a 460px-tall pane is two
            independent scroll areas, and on a phone neither gets enough height
            to show a full row. See the .groups rule in styles.css. */}
        <div className="groups">
          {/* Only when there ARE some. A brand-new family has none, and a
              heading over an empty grid is a promise the screen cannot keep —
              it would read as "your characters are missing", which is the
              complaint this whole section answers. */}
          {ours.length > 0 && (
            <section className="group">
              {/* Not "yours" and "ours" — Jamie's framing was "the characters
                  they chose -v- the pre-baked ones that the app provides", and
                  a parent thinks of these as the ones they made, not as an
                  ownership tier. */}
              <h2 className="ghead">The ones you've made</h2>
              <p className="ghint">
                Already drawn and named — tap for another film with them.
              </p>
              <div className="gallery">
                {ours.map((character) => (
                  <Tile
                    key={character.characterId}
                    art={brokenMine.has(character.characterId) ? null : heroOf(character)}
                    caption={nameOf(character)}
                    noArtTitle="no picture for this one yet"
                    onBroken={() =>
                      setBrokenMine((prev) => new Set(prev).add(character.characterId))
                    }
                    onPick={() => pickMine(character)}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="group">
            {/* The catalog heading exists only to CONTRAST with the one above,
                so it appears only when there is something to contrast with. On
                a first run this screen is exactly what it always was: a grid
                and a text box, with no taxonomy laid over a single group.

                "Ready-made" is the app's own existing word for the catalog —
                Candidates.tsx already says "choose one of the ready-made
                characters" when art fails — so a parent meets one phrase for
                it and not two. */}
            {ours.length > 0 && <h2 className="ghead">Ready-made characters</h2>}
            <div className="gallery">
              {starters.map((starter) => (
                <Tile
                  key={starter.slug}
                  art={brokenArt.has(starter.slug) ? null : portraitOf(starter)}
                  caption={starter.label}
                  noArtTitle="no pre-baked portrait yet"
                  /* DEBUG-only, the way Candidates.tsx already gates its own.
                     Ungated, every tile on the parent's FIRST screen carried a
                     coloured dot whose tooltip read "platform catalog — no cost
                     to you" — our cache tiers, on the surface runtime.ts is
                     emphatic a paying parent must never be shown. */
                  dot={
                    DEBUG ? (
                      <TierDot tier={deviceKnown.has(starter.slug) ? 'device' : 'catalog'} />
                    ) : null
                  }
                  onBroken={() =>
                    // Pre-bake has not run for this starter, or the blob is
                    // gone. Neither should paint a broken icon.
                    setBrokenArt((prev) => new Set(prev).add(starter.slug))
                  }
                  onPick={() => pickStarter(starter)}
                />
              ))}
            </div>
          </section>
        </div>

        <form className="free" onSubmit={submit}>
          <input
            value={text}
            onChange={(e) => onType(e.target.value)}
            placeholder="…or describe your own (try “a purple unicorn in a field”)"
            aria-label="describe your own character"
          />
          <button type="submit">Go</button>
        </form>

        {/* A suggestion chip is already the parent-facing phrasing, so it is
            both the label and the seed for the key. */}
        <GateBoxView box={box} onSuggestion={(text) => pickCustom(text, text)} />
      </div>
    </>
  );
}

/**
 * One tile, whichever grid it is in.
 *
 * Shared rather than written twice because the two grids are the same object
 * to a parent — a picture of somebody, with their name under it — and the
 * broken-art fallback below is the part that would rot if it were duplicated:
 * a family character whose blob has gone must degrade to the same ✦ card a
 * missing starter portrait does, not to the browser's broken-image glyph.
 */
function Tile({
  art,
  caption,
  noArtTitle,
  dot,
  onBroken,
  onPick,
}: {
  art: string | null;
  caption: string;
  noArtTitle: string;
  dot?: React.ReactNode;
  onBroken: () => void;
  onPick: () => void;
}) {
  return (
    <button className="gcard" onClick={onPick}>
      <div style={{ position: 'relative' }}>
        {dot}
        {art ? (
          <img src={art} alt={caption} onError={onBroken} />
        ) : (
          <div className="noart" title={noArtTitle}>
            ✦
          </div>
        )}
      </div>
      <span>{caption}</span>
    </button>
  );
}

/**
 * WHICH OF THE FAMILY'S CHARACTERS MAY BE A TILE, and in what order.
 *
 * `commissionable` is the server's own answer (api/characters.py:_view) and it
 * means status === 'ready': a pose has been chosen and a name locked, both by
 * the single write in _select. Everything else on the list —
 * 'generating_candidates', 'awaiting_selection' — is a wizard run that was
 * abandoned part way, and it is not a character yet in the only sense this
 * screen cares about: POST /commissions refuses it, so its tile would be an
 * offer we cannot honour. The parent would tap it, land on the buy screen, and
 * be charged nothing for a button that fails.
 *
 * It would not even be captionable. _view falls back to the CANONICAL when no
 * name has been locked, so an abandoned draft's tile reads "dragon round soft
 * stubby turquoise baby scale wings" — the sorted token string the aliases
 * table is keyed on. That is the live payload from the deployed API, not a
 * hypothetical (canary family, 2026-08-24).
 *
 * The `??` is for an API old enough not to send the field; the status behind
 * it is the same fact spelled out.
 *
 * NEWEST FIRST, because the character a parent is hunting for is nearly always
 * the one they just made — which is the complaint that produced this section.
 * The server already sorts that way (_list queries the byFamily index with
 * ScanIndexForward=False on createdAt, and the deployed Azure port returns the
 * same order — checked live), so this re-sorts an already-sorted list. It is
 * here because the order is a PRODUCT decision and there are two separate
 * server implementations of it; the sort is stable, so a backend that already
 * agrees is left exactly as it came.
 */
function ownCharacters(characters: Character[]): Character[] {
  return characters
    .filter((c) => c.commissionable ?? c.status === 'ready')
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

/** The locked name. Only ever called on a character ownCharacters() passed. */
function nameOf(character: Character): string {
  return character.characterName || character.name || 'your character';
}

/**
 * The one picture for a character the family already owns.
 *
 * The pose the CHILD tapped, because that is the one they will recognise —
 * _view resolves it into heroUrl (the chosen candidate's art, falling back to
 * the identity portrait). The key fallbacks behind it are for a server with
 * MEDIA_DOMAIN unset, where _media_url returns null rather than a broken URL
 * precisely so the client can compose it instead.
 */
function heroOf(character: Character): string | null {
  return (
    character.heroUrl ??
    mediaUrl(character.heroKey) ??
    character.chosen?.url ??
    character.portraitUrl ??
    mediaUrl(character.portraitKey) ??
    null
  );
}

/**
 * Where the chosen pose sits in `candidates` — a POSITION, not the server's
 * index.
 *
 * Summary.tsx and NameIt.tsx both read `character.candidates[chosenIndex]`, so
 * what they need is an offset into that array, whereas `chosenCandidate` is
 * the index the WORKER stamped on the pose itself. _view sorts the list by
 * that index, so for every character in the product today the two are the same
 * number — but they mean different things, and they come apart the moment a
 * pose is missing from the list. Getting it wrong shows the parent, on the
 * screen where they spend credits, a pose their child did not pick.
 */
function chosenPosition(character: Character): number {
  const wanted = character.chosen?.index ?? character.chosenCandidate;
  if (wanted === undefined || wanted === null) return 0;
  const at = (character.candidates ?? []).findIndex((c) => c.index === wanted);
  return at >= 0 ? at : Number(wanted);
}

function GateBoxView({
  box,
  onSuggestion,
}: {
  box: GateBox;
  onSuggestion: (canonical: string) => void;
}) {
  if (box.kind === 'idle') return null;
  if (box.kind === 'checking') return <div className="gate pulse">checking…</div>;
  if (box.kind === 'error') return <div className="gate">gate unavailable — {box.message}</div>;
  if (box.kind === 'matched') {
    return (
      <div className="gate">
        <b>Matched the catalog — nothing to generate</b>
        {box.label}
      </div>
    );
  }
  if (box.kind === 'prechecked') {
    return (
      <div className="gate">
        <b>Pre-checked while you typed ✓</b>
        {box.verdict.canonical}
      </div>
    );
  }
  // ONLY A CANONICAL IS TAPPABLE. `suggestionCanonical` is the one field the
  // gate promises is a character description; `suggestion` is coaching prose,
  // and today's deployed gate refuses with "Try describing a friendly character
  // like a baby dragon or a fluffy kitten instead." That whole sentence used to
  // be a chip, and one tap made it the character's label AND its cache key — so
  // the parent went straight into the wizard to have a film planned about an
  // instruction. Prose is printed as the advice it is.
  const tappable = box.verdict.suggestionCanonical;
  const advice = box.verdict.suggestion;
  return (
    <div className="gate">
      <b>Let's adjust that one</b>
      {box.verdict.reason}
      {tappable ? (
        <>
          <br />
          <button type="button" className="chip" onClick={() => onSuggestion(tappable)}>
            Try: {tappable}
          </button>
        </>
      ) : (
        advice && <div className="gate-advice">{advice}</div>
      )}
    </div>
  );
}

/**
 * The gate's catalog collapse, and it had never once fired in production.
 *
 * The gate answers `catalog: "purple unicorn"` — lower case, because that is
 * how it canonicalises — and the starter's label is "Purple unicorn". The match
 * was `s.label === verdict.catalog`, so one capital P meant the commonest
 * freetext entry in the product (it is the app's own placeholder text: "try 'a
 * purple unicorn in a field'") never recognised the pre-baked character it names.
 * The parent lost the "nothing to generate" beat and waited for four portraits
 * to be drawn; we paid ~20c for art already sitting in the catalog.
 *
 * Slug too, and slug-with-spaces, because "purple-unicorn" is the other spelling
 * of the same idea and matching on one field was what put us here.
 */
function matchStarter(starters: Starter[] | undefined, wanted: string | null | undefined) {
  const want = wanted?.trim().toLowerCase();
  if (!want || !starters) return undefined;
  return starters.find(
    (s) =>
      s.label.trim().toLowerCase() === want ||
      s.slug.toLowerCase() === want ||
      s.slug.toLowerCase().replace(/-/g, ' ') === want,
  );
}

function portraitOf(starter: Starter): string | null {
  return (
    starter.portraitUrl ??
    mediaUrl(starter.portraitKey) ??
    mediaUrl(starterPortraitKey(starter.slug))
  );
}
